import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router-dom';
import {AppShell} from './components/layout/AppShell';
import {API_ACCESS_PERMISSION, READ_REPORTS_PERMISSION, useAuth} from './context/useAuth';
import {LoginPage} from './pages/LoginPage';
import {DashboardPage} from './pages/DashboardPage';
import {InvestigationsPage} from './pages/InvestigationsPage';
import {SingleVideoSubmissionPage} from './pages/SingleVideoSubmissionPage';
import {PinterestSubmissionPage} from './pages/PinterestSubmissionPage';
import {ItemsPage} from './pages/ItemsPage';
import {SubmissionsPage} from './pages/SubmissionsPage';
import {P0AlertsPage} from './pages/P0AlertsPage';
import {JobsPage} from './pages/JobsPage';
import {JobDetailPage} from './pages/JobDetailPage';
import {QueuesPage} from './pages/QueuesPage';
import {AdminIndexRedirect, AdminPage} from './pages/AdminPage';
import {WorkflowsPage} from './pages/WorkflowsPage';
import {CollectionsPage} from './pages/CollectionsPage';
import {WeeklyQaPage} from './pages/WeeklyQaPage';
import {QcReviewPage} from './pages/QcReviewPage';
import {RejectedSubmissionsPage} from './pages/RejectedSubmissionsPage';
import {ReportingPage} from './pages/ReportingPage';
import {NoAccessPage} from './pages/NoAccessPage';
import {PageNotFound} from './pages/PageNotFound';
import {WhatsNewPage} from './pages/WhatsNewPage';
import {useDocumentTitle} from './lib/documentTitle';
import {DEFAULT_ROUTE, PLATFORM_ID} from './lib/platform';
import {ADMIN_SECTIONS, adminSectionPath} from './lib/adminSections';

// Each deployment targets exactly one platform for its whole build (see lib/platform), so which
// component renders at /submission is effectively fixed per deployment, not something a user ever
// toggles at runtime.
const SubmissionPage =
  PLATFORM_ID === 'pinterest' ? PinterestSubmissionPage : SingleVideoSubmissionPage;

function ProtectedRoute() {
  const {isAuthenticated, isLoading, user} = useAuth();
  const location = useLocation();
  // Carries the page the user was trying to reach (e.g. a Slack "Review Now"/"View Case" deep
  // link) through sign-in - see LoginPage, which reads this back to redirect there instead of
  // the default landing page once authenticated.
  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Navigate replace to="/login" state={{from: `${location.pathname}${location.search}`}} />
    );
  }

  // Auth0 authenticated this user but granted them nothing, so every API endpoint 403s regardless
  // of route - stop before the app shell mounts rather than showing a dashboard that can only ever
  // error. Both halves matter: the access permission is the API's global gate, and the role is what
  // the per-controller checks read, so a user missing either can't use the app.
  if (!user?.role || !user.permissions?.includes(API_ACCESS_PERMISSION)) {
    return <NoAccessPage />;
  }

  return <Outlet />;
}

// Admin-only routes redirect non-admins away rather than mounting the page, which would
// otherwise fire /admin/* requests that 403 (and retry) for analysts.
function AdminRoute() {
  const {user, isLoading} = useAuth();
  if (isLoading) {
    return null;
  }

  return user?.role === 'admin' ? <Outlet /> : <Navigate replace to={DEFAULT_ROUTE} />;
}

// Broad-view routes redirect plain analysts away, same rationale as AdminRoute.
function BroadViewRoute() {
  const {user, isLoading} = useAuth();
  if (isLoading) {
    return null;
  }

  return user?.role === 'admin' || user?.role === 'analyst_admin' ? (
    <Outlet />
  ) : (
    <Navigate replace to={DEFAULT_ROUTE} />
  );
}

function PermissionRoute({permission}: {readonly permission: string}) {
  const {user, isLoading} = useAuth();
  if (isLoading) {
    return null;
  }

  return user?.permissions.includes(permission) ? (
    <Outlet />
  ) : (
    <Navigate replace to={DEFAULT_ROUTE} />
  );
}

export function App() {
  useDocumentTitle();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate replace to={DEFAULT_ROUTE} />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="submission" element={<SubmissionPage />} />
          <Route path="investigations" element={<InvestigationsPage />} />
          <Route path="content" element={<ItemsPage />} />
          <Route path="submissions" element={<SubmissionsPage />} />
          <Route path="rejected-submissions" element={<RejectedSubmissionsPage />} />
          <Route path="p0-alerts" element={<P0AlertsPage />} />
          <Route path="p0-alerts/:id" element={<P0AlertsPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/:id" element={<JobDetailPage />} />
          <Route path="whats-new" element={<WhatsNewPage />} />
          <Route path="admin/workflows" element={<WorkflowsPage />} />
          <Route element={<PermissionRoute permission="access:queues" />}>
            <Route path="queues" element={<QueuesPage />} />
          </Route>
          <Route element={<BroadViewRoute />}>
            <Route path="weekly-qa" element={<WeeklyQaPage />} />
            <Route path="qc-review" element={<QcReviewPage />} />
            <Route path="admin" element={<AdminIndexRedirect />} />
            {/* AdminPage itself narrows to the toggle-only taxonomy view for analyst_admin. */}
            <Route path="admin/classifications" element={<AdminPage section="classifications" />} />
          </Route>
          <Route element={<PermissionRoute permission={READ_REPORTS_PERMISSION} />}>
            <Route path="reporting" element={<ReportingPage />} />
            <Route path="reporting/:reportId" element={<ReportingPage />} />
          </Route>
          <Route element={<AdminRoute />}>
            <Route path="collections" element={<CollectionsPage />} />
            {ADMIN_SECTIONS.filter((section) => !section.broadViewOnly).map((section) => (
              <Route
                key={section.slug}
                path={`admin/${section.slug}`}
                element={<AdminPage section={section.slug} />}
              />
            ))}
          </Route>
          {/* Retired paths that bookmarks may still carry: each goes to the page that replaced it
              rather than to the 404 below. */}
          <Route path="single-submission" element={<Navigate replace to="/submission" />} />
          <Route
            path="classifications"
            element={<Navigate replace to={adminSectionPath('classifications')} />}
          />
          {/* A bad link inside the app is shown as one, in the shell, rather than silently landing
              on DEFAULT_ROUTE. Logged out, ProtectedRoute still sends it to /login first. */}
          <Route path="*" element={<PageNotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
